import { useState, useRef, useEffect, useCallback } from "react";
import { Box, Card, Typography, ToggleButton, ToggleButtonGroup, useTheme, IconButton, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Info, Check } from "lucide-react";
import { useTranslation } from "../../../../language";
import CustomDialogSmall from "../../../../components/Dialog/DiaCompSmall";
import TextInfoComp from "../ReuseComps/TextInfoComp";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function statusColor(theme, status) {
  if (!status) return theme.palette.grey[400];
  const s = status.toUpperCase();
  if (s === "NORMAL" || s === "EXPECTED" || s === "THERAPEUTIC") return theme.palette.traffic.green;
  return theme.palette.traffic.red;
}

function GaugeBar({ value, low, high }) {
  const theme = useTheme();
  if (value == null || (low == null && high == null)) return null;
  const val = Number(value);
  const lo  = low  != null ? Number(low)  : null;
  const hi  = high != null ? Number(high) : null;
  let dMin, dMax;
  if (lo != null && hi != null) {
    const range = (hi - lo) || 1;
    dMin = lo - range * 0.5;
    dMax = hi + range * 0.5;
  } else if (lo != null) {
    dMin = Math.min(0, lo * 0.5);
    dMax = Math.max(lo * 2, val * 1.2);
  } else {
    dMin = Math.min(0, val * 0.5);
    dMax = Math.max(hi * 1.5, val * 1.2);
  }
  const pad  = (dMax - dMin) * 0.05 || 1;
  dMin = Math.min(dMin, val - pad);
  dMax = Math.max(dMax, val + pad);
  const span     = dMax - dMin;
  const clamp    = v => Math.max(0, Math.min(100, ((v - dMin) / span) * 100));
  const lowPct   = lo != null ? clamp(lo) : 0;
  const highPct  = hi != null ? clamp(hi) : 100;
  const valPct   = clamp(val);
  const isNormal = (lo == null || val >= lo) && (hi == null || val <= hi);
  return (
    <Box sx={{ position: "relative", width: "100%", pb: 1 }}>
      <Box sx={{ position: "relative", width: "100%", height: 5, bgcolor: "action.disabledBackground", borderRadius: 0.5 }}>
        <Box sx={{ position: "absolute", left: `${lowPct}%`, width: `${highPct - lowPct}%`, height: "100%", bgcolor: theme.palette.traffic.green, opacity: 0.4 }} />
        <Box sx={{ position: "absolute", top: "50%", left: `${valPct}%`, transform: "translate(-50%, -50%)", width: 4, height: 14, bgcolor: isNormal ? theme.palette.traffic.green : theme.palette.traffic.red }} />
      </Box>
      {lo != null && (
        <Typography variant="caption" color="text.disabled" sx={{ position: "absolute", top: "100%", left: `${lowPct}%`, transform: "translateX(-50%)", fontSize: "0.6rem", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {lo.toFixed(2)}
        </Typography>
      )}
      {hi != null && (
        <Typography variant="caption" color="text.disabled" sx={{ position: "absolute", top: "100%", left: `${highPct}%`, transform: "translateX(-50%)", fontSize: "0.6rem", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {hi.toFixed(2)}
        </Typography>
      )}
    </Box>
  );
}

// ─── Marker definitions ───────────────────────────────────────────────────────
// curve: [xFrac, meanYFrac, loYFrac, hiYFrac]
//   xFrac 0–1 spans the full timeline (peri → postmeno)
//   yFrac 0 = top (high value), 1 = bottom (zero)
// inferX(value, age) → most probable xFrac for dot placement
// Colors are computed inside the component via useTheme() → MARKER_COLORS

const MARKERS = {
  fsh: {
    label: "FSH",
    unit: "mIU/ml",
    yMax: 140,
    curve: [
      [0.00, 0.82, 0.90, 0.70],[0.07, 0.76, 0.84, 0.64],[0.14, 0.68, 0.77, 0.56],
      [0.21, 0.60, 0.70, 0.48],[0.28, 0.52, 0.63, 0.39],[0.35, 0.46, 0.57, 0.33],
      [0.42, 0.42, 0.53, 0.29],[0.50, 0.40, 0.51, 0.27],[0.60, 0.40, 0.51, 0.27],
      [0.65, 0.40, 0.51, 0.27],[0.74, 0.39, 0.50, 0.26],[0.84, 0.38, 0.49, 0.25],
      [0.92, 0.37, 0.48, 0.25],[1.00, 0.36, 0.47, 0.24],
    ],
    hrtMean: [[0.42,0.42],[0.55,0.40],[0.65,0.40],[0.80,0.39],[1.00,0.37]],
    hrtBand: [[0.42,0.46,0.35],[0.55,0.44,0.33],[0.65,0.44,0.33],[0.80,0.43,0.32],[1.00,0.41,0.30]],
    inferX(value, age = 52) {
      const ao = Math.max(0, Math.min(0.15, (age - 50) * 0.01));
      if (value < 45)  return Math.min(0.60, 0.48 + ao);
      if (value < 60)  return Math.min(0.68, 0.56 + ao);
      if (value < 80)  return Math.min(0.78, 0.68 + ao);
      if (value < 100) return Math.min(0.88, 0.78 + ao);
      return Math.min(0.95, 0.86 + ao);
    },
  },
  lh: {
    label: "LH",
    unit: "mIU/ml",
    yMax: 80,
    curve: [
      [0.00, 0.82, 0.90, 0.72],[0.07, 0.76, 0.84, 0.66],[0.14, 0.69, 0.78, 0.58],
      [0.21, 0.61, 0.71, 0.50],[0.28, 0.54, 0.65, 0.42],[0.35, 0.49, 0.60, 0.37],
      [0.42, 0.45, 0.56, 0.33],[0.50, 0.44, 0.54, 0.31],[0.60, 0.44, 0.54, 0.31],
      [0.65, 0.44, 0.54, 0.31],[0.74, 0.43, 0.53, 0.30],[0.84, 0.43, 0.53, 0.30],
      [0.92, 0.43, 0.53, 0.30],[1.00, 0.43, 0.53, 0.30],
    ],
    hrtMean: [[0.42,0.45],[0.55,0.44],[0.65,0.44],[0.80,0.43],[1.00,0.43]],
    hrtBand: [[0.42,0.49,0.38],[0.55,0.48,0.37],[0.65,0.48,0.37],[0.80,0.47,0.36],[1.00,0.47,0.36]],
    inferX(value, age = 52) {
      const ao = Math.max(0, Math.min(0.15, (age - 50) * 0.01));
      if (value < 16)  return Math.min(0.60, 0.48 + ao);
      if (value < 22)  return Math.min(0.68, 0.58 + ao);
      if (value < 32)  return Math.min(0.78, 0.70 + ao);
      if (value < 42)  return Math.min(0.88, 0.80 + ao);
      return Math.min(0.95, 0.88 + ao);
    },
  },
  e2: {
    label: "E2",
    unit: "pg/ml",
    yMax: 220,
    curve: [
      [0.00, 0.25, 0.10, 0.42],[0.05, 0.44, 0.28, 0.62],[0.10, 0.18, 0.06, 0.36],
      [0.15, 0.48, 0.30, 0.65],[0.20, 0.22, 0.09, 0.40],[0.25, 0.52, 0.35, 0.68],
      [0.28, 0.65, 0.52, 0.78],[0.35, 0.78, 0.66, 0.88],[0.42, 0.87, 0.78, 0.94],
      [0.50, 0.88, 0.80, 0.94],[0.60, 0.88, 0.80, 0.94],[0.65, 0.88, 0.80, 0.94],
      [0.74, 0.88, 0.80, 0.94],[0.84, 0.88, 0.80, 0.94],[1.00, 0.88, 0.80, 0.94],
    ],
    hrtMean: [[0.42,0.86],[0.50,0.52],[0.60,0.46],[0.65,0.46],[0.80,0.46],[1.00,0.48]],
    hrtBand: [[0.42,0.90,0.78],[0.50,0.64,0.36],[0.60,0.58,0.30],[0.65,0.58,0.30],[0.80,0.58,0.30],[1.00,0.60,0.32]],
    inferX(value, age = 52) {
      const ao = Math.max(0, Math.min(0.18, (age - 50) * 0.012));
      if (value > 100) return Math.min(0.62, 0.50 + ao * 0.5);
      if (value > 50)  return Math.min(0.68, 0.56 + ao * 0.5);
      if (value > 25)  return Math.min(0.78, 0.68 + ao);
      return Math.min(0.90, 0.78 + ao);
    },
  },
  p4: {
    label: "P4",
    unit: "ng/ml",
    yMax: 22,
    curve: [
      [0.00, 0.55, 0.70, 0.36],[0.06, 0.68, 0.82, 0.50],[0.12, 0.60, 0.75, 0.42],
      [0.18, 0.74, 0.87, 0.58],[0.22, 0.80, 0.91, 0.66],[0.28, 0.88, 0.94, 0.78],
      [0.35, 0.92, 0.96, 0.85],[0.42, 0.94, 0.97, 0.88],[0.50, 0.95, 0.97, 0.90],
      [0.60, 0.95, 0.97, 0.90],[0.65, 0.95, 0.97, 0.90],[0.74, 0.95, 0.97, 0.90],
      [0.84, 0.95, 0.97, 0.90],[1.00, 0.95, 0.97, 0.90],
    ],
    hrtMean: [[0.42,0.94],[0.50,0.60],[0.60,0.55],[0.65,0.55],[0.80,0.56],[1.00,0.58]],
    hrtBand: [[0.42,0.97,0.88],[0.50,0.72,0.44],[0.60,0.68,0.38],[0.65,0.68,0.38],[0.80,0.69,0.40],[1.00,0.70,0.42]],
    inferX(value, age = 52) {
      const ao = Math.max(0, Math.min(0.15, (age - 50) * 0.01));
      if (value > 8)   return Math.min(0.60, 0.50 + ao * 0.4);
      if (value > 3)   return Math.min(0.70, 0.58 + ao * 0.6);
      if (value > 0.8) return Math.min(0.80, 0.70 + ao);
      return Math.min(0.92, 0.80 + ao);
    },
  },
};

// ─── Phase band layout (colors computed in component via theme) ───────────────
const PHASES = [
  { id: "peri",      x: 0,    w: 0.28 },
  { id: "menopause", x: 0.28, w: 0.14 },
  { id: "postmeno",  x: 0.42, w: 0.58 },
];

// ─── SVG path helpers ─────────────────────────────────────────────────────────
function smoothPath(points) {
  if (!points.length) return "";
  let d = `M${points[0][0].toFixed(1)},${points[0][1].toFixed(1)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const mx = ((points[i][0] + points[i + 1][0]) / 2).toFixed(1);
    d += ` C${mx},${points[i][1].toFixed(1)} ${mx},${points[i + 1][1].toFixed(1)} ${points[i + 1][0].toFixed(1)},${points[i + 1][1].toFixed(1)}`;
  }
  return d;
}

function areaPath(topPoints, bottomPoints) {
  return `${smoothPath(topPoints)} ${smoothPath([...bottomPoints].reverse()).replace("M", "L")} Z`;
}

// ─── Legend item ──────────────────────────────────────────────────────────────
function LegendItem({ type, color, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      {type === "band"   && <Box sx={{ width: 20, height: 8, borderRadius: 0.5, bgcolor: color, opacity: 0.4, border: `1px solid ${color}` }} />}
      {type === "dashed" && <Box sx={{ width: 20, borderTop: `2.5px dashed ${color}` }} />}
      <Typography variant="caption" sx={{ color: "text.disabled" }}>{label}</Typography>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
/**
 * MenopauseHormoneChart
 *
 * Props:
 *   markers   {Object}  – { fsh, lh, e2, p4 } each: { value, unit, status }
 *   age       {number}  – user age, refines dot x-placement
 */
export default function MenopauseHormoneChart({ markers: markerResults = {}, age = 52 }) {
  const [activeMarker, setActiveMarker]       = useState("fsh");
  const [infoOpen, setInfoOpen]               = useState(false);
  const [markerInfoOpen, setMarkerInfoOpen]   = useState(false);
  const [markerInfoShort, setMarkerInfoShort] = useState(null);
  const theme = useTheme();
  const { t } = useTranslation();
  const svgRef = useRef(null);
  const [dims, setDims] = useState({ width: 560, height: 200 });

  useEffect(() => {
    if (!svgRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0].contentRect.width;
      setDims({ width: w, height: Math.round(w * (200 / 560)) });
    });
    ro.observe(svgRef.current.parentElement);
    return () => ro.disconnect();
  }, []);

  // ── Theme-derived colors ──────────────────────────────────────────────────
  // Each hormone gets a distinct palette family: FSH=primary (warm), LH=traffic.orange,
  // E2=tertiary (teal, calm/falling), P4=quartiary (purple, calm/sleep)
  const MARKER_COLORS = {
    fsh: theme.palette.primary.C600,
    lh:  theme.palette.traffic.orange,
    e2:  theme.palette.tertiary.C700,
    p4:  theme.palette.quartiary.C600,
  };

  // HRT reference band — muted teal, distinct from all four marker colors
  const HRT_COLOR = theme.palette.tertiary.C400;

  // Phase band fills and label tints — each phase maps to the same palette as
  // its conceptually related hormone (peri≈primary warm, meno≈quartiary purple, postmeno≈tertiary teal)
  const PHASE_STYLES = {
    peri:      { fill: theme.palette.primary.C300,   tc: theme.palette.primary.C800   },
    menopause: { fill: theme.palette.quartiary.C300, tc: theme.palette.quartiary.C800 },
    postmeno:  { fill: theme.palette.tertiary.C300,  tc: theme.palette.tertiary.C800  },
  };

  // SVG structural chrome — use text-token derived colors for light/dark adaptability
  const gridColor    = alpha(theme.palette.text.primary, 0.07);
  const dividerColor = alpha(theme.palette.text.primary, 0.18);
  const axisColor    = theme.palette.text.disabled;
  const dotRingColor = theme.palette.background.paper;

  const { width: W, height: H } = dims;
  const PL = 42, PT = 22, PR = 8, PB = 10;
  const CW = W - PL - PR;
  const CH = H - PT - PB;

  const cx = useCallback((f) => PL + f * CW, [CW]);
  const cy = useCallback((f) => PT + f * CH, [CH, PT]);

  const marker      = MARKERS[activeMarker];
  const markerColor = MARKER_COLORS[activeMarker];
  const result      = { ...(markerResults[activeMarker] || {}) };

  const knownStatus = {
    NORMAL:      t("bt_status_normal"),
    LOW:         t("bt_status_low"),
    HIGH:        t("bt_status_high"),
    EXPECTED:    t("mhc_status_expected"),
    THERAPEUTIC: t("mhc_status_therapeutic"),
  };
  const statusText = knownStatus[result?.status] ?? result?.status ?? "";

  const cardStyles = theme.palette.mode === "light"
    ? { bgcolor: "grey.C50", borderColor: "grey.C900" }
    : { bgcolor: "greydark.C900", borderColor: "greydark.C400", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", boxShadow: "0 8px 32px rgba(0,0,0,0.6)" };

  // Dot position
  const dotXFrac = result?.value != null ? marker.inferX(result.value, age) : 0.55;
  const dotYFrac = result?.value != null ? 1 - Math.min(1, Math.max(0, result.value / marker.yMax)) : 0.5;
  const dotX = cx(dotXFrac);
  const dotY = cy(dotYFrac);

  // SVG point arrays
  const hiPts      = marker.curve.map(([xf,,,hi]) => [cx(xf), cy(hi)]);
  const loPts      = marker.curve.map(([xf,,lo])   => [cx(xf), cy(lo)]);
  const meanPts    = marker.curve.map(([xf,mn])     => [cx(xf), cy(mn)]);
  const hrtHiPts   = marker.hrtBand?.map(([xf,,hi]) => [cx(xf), cy(hi)]) ?? [];
  const hrtLoPts   = marker.hrtBand?.map(([xf,lo])  => [cx(xf), cy(lo)]) ?? [];
  const hrtMeanPts = marker.hrtMean?.map(([xf,yf])  => [cx(xf), cy(yf)]) ?? [];
  const hrtLabelPt = hrtMeanPts.length >= 3 ? hrtMeanPts[hrtMeanPts.length - 2] : null;
  const labelAboveDot = dotYFrac > 0.12;

  return (
    <Box>

      {/* ── Section header ─────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2.25 }}>
        <Box sx={{ width: 3, height: 18, borderRadius: 0.5, bgcolor: markerColor, flexShrink: 0 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary", letterSpacing: "-0.01em" }}>
          {t("mhc_title")}
        </Typography>
        <IconButton size="small" onClick={() => setInfoOpen(true)} sx={{ ml: 0.25, color: "text.disabled" }}>
          <Info size={18} />
        </IconButton>
      </Box>

      <CustomDialogSmall
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        title={t("mhc_title")}
        content={null}
      />

      {/* ── Chart card ─────────────────────────────────────────────────── */}
      <Card variant="outlined" sx={{ ...cardStyles, borderRadius: 4, px: "14px", pt: "12px", pb: "8px", mb: "14px" }}>

        {/* Marker selector */}
        <Typography variant="caption" sx={{ display: "block", fontWeight: 600, color: "text.secondary", mb: 0.75 }}>
          {t("mhc_select_marker")}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25 }}>
          <ToggleButtonGroup
            value={activeMarker}
            exclusive
            onChange={(_, val) => val && setActiveMarker(val)}
            size="small"
            sx={{
              gap: 0.5,
              "& .MuiToggleButtonGroup-grouped": { border: "none", borderRadius: "9px !important" },
            }}
          >
            {Object.keys(MARKERS).map(id => {
              const mc = MARKER_COLORS[id];
              return (
                <ToggleButton
                  key={id}
                  value={id}
                  sx={{
                    px: 1.5, py: 0.6,
                    fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
                    bgcolor: activeMarker === id ? mc : alpha(theme.palette.text.primary, 0.06),
                    color: activeMarker === id ? "common.white" : "text.secondary",
                    "&.Mui-selected": {
                      bgcolor: mc,
                      color: "common.white",
                      "&:hover": { bgcolor: alpha(mc, 0.85) },
                    },
                    "&:hover": { bgcolor: alpha(theme.palette.text.primary, 0.1) },
                  }}
                >
                  {MARKERS[id].label}
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>
          <Typography variant="caption" sx={{ color: "text.disabled" }}>
            {marker.label} · {t(`mhc_role_${activeMarker}`)}
          </Typography>
        </Box>

        {/* SVG chart */}
        <Box ref={svgRef} sx={{ width: "100%" }}>
          <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto", display: "block", overflow: "visible" }}>
            <defs>
              <clipPath id="mhc-clip">
                <rect x={PL} y={PT} width={CW} height={CH} />
              </clipPath>
            </defs>

            {/* Phase bands */}
            {PHASES.map((p) => (
              <g key={p.id}>
                <rect x={cx(p.x)} y={PT} width={p.w * CW} height={CH} fill={PHASE_STYLES[p.id].fill} opacity={0.28} rx={2} />
                <text
                  x={cx(p.x + p.w / 2)} y={PT - 6}
                  textAnchor="middle" fontSize={7.5} fontFamily="DM Sans, sans-serif"
                  fontWeight={600} fill={PHASE_STYLES[p.id].tc} letterSpacing={0.04}
                >
                  {t(`mhc_phase_${p.id}`).toUpperCase()}
                </text>
              </g>
            ))}

            {/* Grid + Y labels */}
            {[0, 0.25, 0.5, 0.75, 1].map((f) => (
              <g key={f}>
                <line x1={PL} y1={cy(f)} x2={W - PR} y2={cy(f)} stroke={gridColor} strokeWidth={0.8} />
                <text x={PL - 4} y={cy(f) + 3} textAnchor="end" fontSize={8} fontFamily="DM Sans, sans-serif" fill={axisColor}>
                  {Math.round(marker.yMax * (1 - f))}
                </text>
              </g>
            ))}

            {/* Phase dividers */}
            {PHASES.slice(1).map((p) => (
              <line key={`d-${p.id}`} x1={cx(p.x)} y1={PT} x2={cx(p.x)} y2={PT + CH} stroke={dividerColor} strokeWidth={0.7} strokeDasharray="3,3" />
            ))}

            <g clipPath="url(#mhc-clip)">
              <path d={areaPath(hiPts, loPts)} fill={markerColor} opacity={0.16} />
              <path d={smoothPath(meanPts)} fill="none" stroke={markerColor} strokeWidth={2} strokeDasharray="6,4" strokeLinecap="round" />
              {hrtHiPts.length > 0 && <path d={areaPath(hrtHiPts, hrtLoPts)} fill={HRT_COLOR} opacity={0.20} />}
              {hrtMeanPts.length > 0 && <path d={smoothPath(hrtMeanPts)} fill="none" stroke={HRT_COLOR} strokeWidth={1.8} strokeDasharray="6,4" strokeLinecap="round" />}
              {hrtLabelPt && (
                <text x={hrtLabelPt[0]} y={hrtLabelPt[1] - 7} textAnchor="middle" fontSize={7} fontFamily="DM Sans, sans-serif" fill={HRT_COLOR} fontWeight={600} letterSpacing={0.05}>
                  {t("mhc_hrt_label")}
                </text>
              )}
              <line x1={dotX} y1={PT} x2={dotX} y2={dotY} stroke={markerColor} strokeWidth={0.8} strokeDasharray="3,3" opacity={0.45} />
              <circle cx={dotX} cy={dotY} r={9} fill="none" stroke={markerColor} strokeWidth={1} opacity={0.25} />
              <circle cx={dotX} cy={dotY} r={5.5} fill={markerColor} stroke={dotRingColor} strokeWidth={2.5} />
              {result?.value != null && (
                <text x={dotX} y={labelAboveDot ? dotY - 14 : dotY + 20} textAnchor="middle" fontSize={9.5} fontFamily="DM Sans, sans-serif" fontWeight={700} fill={markerColor}>
                  {result.value} {result.unit ?? marker.unit}
                </text>
              )}
            </g>
          </svg>
        </Box>

        {/* Legend */}
        <Box sx={{ display: "flex", gap: 1.75, flexWrap: "wrap", mt: 1.25, pt: 1.25, borderTop: "1px solid", borderColor: "divider", alignItems: "center" }}>
          <LegendItem type="band"   color={markerColor} label={t("mhc_legend_range")} />
          <LegendItem type="dashed" color={markerColor} label={t("mhc_legend_mean")} />
          <LegendItem type="dashed" color={HRT_COLOR}   label={t("mhc_legend_hrt")} />
        </Box>
      </Card>

      {/* ── Marker row ─────────────────────────────────────────────────── */}
      {result?.value != null && (
        <Box sx={{
          bgcolor: theme.palette.mode === "light" ? "grey.C50" : "greydark.C900",
          borderRadius: "14px",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", px: 2.5, py: 1.25, gap: 2, flexWrap: { xs: "wrap", sm: "nowrap" } }}>
            <Box sx={{ width: 10, height: 30, flexShrink: 0, bgcolor: statusColor(theme, result.status) }} />
            <Box sx={{ flexGrow: 1, minWidth: 120 }}>
              <Typography variant="body1" fontWeight={500}>{t(`markername_${result.marker_short}`)}</Typography>
              <Typography variant="caption" color="text.secondary">{t(`markerdescr_${result.marker_short}`)}</Typography>
            </Box>
            <Typography variant="number3" fontWeight={400} sx={{ minWidth: 80, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
              {Number(result.value).toFixed(2)}{result.unit ? ` ${result.unit}` : ""}
            </Typography>
            <Box sx={{ minWidth: 140, ml: 2, display: "flex", alignItems: "center" }}>
              <GaugeBar value={result.value} low={result.ref_low} high={result.ref_high} />
            </Box>
            <Box sx={{ minWidth: 68, display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
              {["NORMAL", "EXPECTED", "THERAPEUTIC"].includes(result.status)
                ? <Check size={18} strokeWidth={3} color={theme.palette.traffic.green} />
                : <Typography variant="subtitle2" fontWeight={700} sx={{ color: statusColor(theme, result.status) }}>{statusText}</Typography>
              }
            </Box>
            <Tooltip title="Info" placement="right">
              <IconButton size="small" onClick={() => { setMarkerInfoShort(result.marker_short); setMarkerInfoOpen(true); }} sx={{ "&:hover": { transform: "scale(1.2)" } }}>
                <Info size={18} color={theme.palette.secondary.main} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      <CustomDialogSmall
        open={markerInfoOpen}
        onClose={() => { setMarkerInfoOpen(false); setMarkerInfoShort(null); }}
        title="Information"
        content={markerInfoShort && (
          <TextInfoComp
            description={`description_${markerInfoShort}`}
            lowtext={`lowtext_${markerInfoShort}`}
            lowrec={`lowrec_${markerInfoShort}`}
            rangetext={`rangetext_${markerInfoShort}`}
            hightext={`hightext_${markerInfoShort}`}
            highrec={`highrec_${markerInfoShort}`}
          />
        )}
      />
    </Box>
  );
}
